import {
  ArrowUpRight,
  Edit3,
  Copy,
  Share2,
  Star,
  Folder,
  Trash2,
  Check,
} from '@/components/icons';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '../ui/beui';
import type { DiagramMetadata, Folder as FolderType } from '../../types';
import { DiagramIcon } from '../diagramIcons';

interface DiagramRowContextMenuProps {
  diagram: DiagramMetadata;
  folders: FolderType[];
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onToggleStar: () => void;
  onMove: (folderId: string | null) => void;
  onShare: () => void;
  onDelete: () => void;
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
}

/**
 * Right-click actions for a diagram row on the dashboard. Mirrors DiagramRowMenu
 * (the ⋯ button) so both entry points offer the same accessible menu.
 */
export function DiagramRowContextMenu({
  diagram,
  folders,
  onOpen,
  onRename,
  onDuplicate,
  onToggleStar,
  onMove,
  onShare,
  onDelete,
  children,
}: DiagramRowContextMenuProps) {
  return (
    <ContextMenu>
      <ContextMenuTrigger>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ContextMenuItem onSelect={onOpen}>
          <ArrowUpRight /> Open
        </ContextMenuItem>
        <ContextMenuItem onSelect={onRename}>
          <Edit3 /> Rename &amp; settings…
        </ContextMenuItem>
        <ContextMenuItem onSelect={onDuplicate}>
          <Copy /> Duplicate
        </ContextMenuItem>
        <ContextMenuItem onSelect={onShare}>
          <Share2 /> Share…
        </ContextMenuItem>
        <ContextMenuItem onSelect={onToggleStar}>
          <Star className={diagram.isStarred ? 'fill-amber-500 text-amber-500' : ''} />
          {diagram.isStarred ? 'Remove star' : 'Star'}
        </ContextMenuItem>

        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Folder /> Move to
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            <ContextMenuItem onSelect={() => onMove(null)} disabled={!diagram.folderId}>
              <span className="flex-1">No folder</span>
              {!diagram.folderId && <Check className="text-primary" />}
            </ContextMenuItem>
            {folders.length > 0 && <ContextMenuSeparator />}
            {folders.map((f) => {
              const active = diagram.folderId === f.id;
              return (
                <ContextMenuItem key={f.id} onSelect={() => onMove(f.id)} disabled={active}>
                  <DiagramIcon name={f.icon} color={f.color} className="h-4 w-4 flex-none" />
                  <span className="flex-1 truncate">{f.name}</span>
                  {active && <Check className="text-primary" />}
                </ContextMenuItem>
              );
            })}
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator />
        <ContextMenuItem tone="destructive" onSelect={onDelete}>
          <Trash2 /> Delete…
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
